import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

import type { PipelineCard, PipelineSummaryCards } from '@control-center/shared';

import { formatPipelineMoney } from '../../../lib/pipeline-helpers';

interface PipelineCardsProps {
  summary: PipelineSummaryCards;
}

/**
 * Cards de topo do dashboard de pipeline (Story 11.12 AC2). Reusa o padrão visual
 * do KpiCard (E6) — tokens do design system, variação vs período anterior em
 * verde/vermelho. `total_in_negotiation` pode ser null (role sem valores — AC8).
 */
export function PipelineCards({ summary }: PipelineCardsProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {summary.total_in_negotiation ? (
        <Card label="Total em negociação" card={summary.total_in_negotiation} format="money" />
      ) : (
        <RestrictedCard label="Total em negociação" />
      )}
      <Card label="Deals abertos" card={summary.open_deals} format="count" />
      <Card label="Propostas a vencer (7 dias)" card={summary.proposals_expiring} format="count" />
      <Card label="Leads quentes sem ação" card={summary.hot_leads_no_action} format="count" />
    </div>
  );
}

function Card({
  label,
  card,
  format,
}: {
  label: string;
  card: PipelineCard;
  format: 'money' | 'count';
}): JSX.Element {
  const trendColor =
    card.trend === 'up' ? 'text-good' : card.trend === 'down' ? 'text-bad' : 'text-muted-2';
  const trendIcon =
    card.trend === 'up' ? (
      <ArrowUpRight className="h-3.5 w-3.5" />
    ) : card.trend === 'down' ? (
      <ArrowDownRight className="h-3.5 w-3.5" />
    ) : (
      <Minus className="h-3.5 w-3.5" />
    );
  const deltaLabel =
    card.delta_pct === null
      ? '—'
      : `${card.delta_pct >= 0 ? '+' : ''}${card.delta_pct.toFixed(1)}%`;

  const display =
    format === 'money' ? formatPipelineMoney(card.value) : card.value.toLocaleString('pt-BR');

  return (
    <div className="rounded-lg border border-line bg-panel p-4 shadow-md">
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className="mt-2 font-mono text-[32px] leading-none tracking-tight text-ink tabular-nums">
        {display}
      </div>
      <div className={`mt-3 flex items-center gap-1 text-xs font-medium ${trendColor}`}>
        {trendIcon}
        <span>{deltaLabel}</span>
        <span className="ml-1 text-muted-3">vs período anterior</span>
      </div>
    </div>
  );
}

/** Card com valor restrito por role (suporte não vê valores estratégicos — AC8). */
function RestrictedCard({ label }: { label: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-line bg-panel p-4 shadow-md">
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className="mt-2 font-mono text-[32px] leading-none tracking-tight text-muted-3 tabular-nums">
        —
      </div>
      <div className="mt-3 text-xs text-muted-3">Restrito ao seu perfil</div>
    </div>
  );
}
